import * as React from 'react'
import { useEffect, useState } from 'react'
import { PlatformMessageDto } from '@halo/dtos/platform-message.dto'
import { DatePicker, Flex, Form, Input, Select, Switch } from 'antd'
import { AppService } from '@halo/services/app.service'
import { AppState } from '@halo/stores/app.state'
import { Container } from 'typescript-ioc'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import { Drawer, Well } from '@pushly/aqe/lib/components'
import { Editor } from 'react-draft-wysiwyg'
import { EditorState, convertToRaw } from 'draft-js'
import draftToHtml from 'draftjs-to-html/lib/draftjs-to-html'
import { classnames } from '@halo/_utils/classnames'
import './platform-messages.scss'
import * as moment from 'moment'
import { Moment } from 'moment'
import { DomainService } from '@halo/services/domain.service'
import { titleCase } from '@pushly/models/lib/utils/change-case'
import deepEqual from 'react-fast-compare'
import { simpleFormErrorNotification } from '@pushly/aqe/lib/utils'
import { TAGS } from '@halo/enums/message-tags'

interface IPlatformMessageEditor {
    allMessages: PlatformMessageDto[]
    message?: any
    visible: boolean
    onClose?: (e: any) => void
    onMessageUpdated?: () => void
}

export function PlatformMessageEditor(props: IPlatformMessageEditor) {
    const [form] = Form.useForm()

    const appState = React.useMemo(() => Container.get(AppState), [])
    const appService = React.useMemo(() => Container.get(AppService), [])
    const domainService = React.useMemo(() => Container.get(DomainService), [])

    const [editorState, setEditorState] = useState<any>(
        props.message?.message || EditorState.createEmpty(),
    )
    const [domainRestricted, setDomainRestricted] = useState<boolean>(
        props.message?.domain_ids?.length > 0 || false,
    )
    const [useMinVersionReq, setUseMinVersionReq] = useState<boolean>(!!props.message?.min_version_req)
    const [useMaintenanceMode, setUseMaintenanceMode] = useState<boolean>(false)
    const [domains, setDomains] = useState<any[]>([])
    const [versions, setVersions] = useState<any[]>([])

    const message = props.message?.message
    const minVersion = props.message?.min_version_req
    const isEditMode = props.message?.id !== undefined

    useEffect(() => {
        fetchDependencies()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    async function fetchDependencies(): Promise<void> {
        const domainFields = ['id', 'display_name']

        const { ok, data: fetchedDomains } = await domainService.fetchAll({
            showLoadingScreen: false,
            query: {
                pagination: false,
                fields: domainFields.join(','),
            },
        })

        const { versions: fetchedVersions } = await appService.getApplicationVersions()

        if (ok) {
            setDomains(fetchedDomains)
            setVersions(fetchedVersions)
        }
    }

    const validateEndDate = async (_rule: any, value: Moment): Promise<void> => {
        if (!useMaintenanceMode || !!value) {
            if (value) {
                const error = value <= form.getFieldValue('startTime')
                if (error) {
                    throw new Error('End Date must be a date/time which occurs after the specified Start Time.')
                }
            }
            return
        }
        throw new Error('End Date is required in maintenance mode.')
    }

    const validateMessage = async (_rule: any, value: any): Promise<void> => {
        const html = draftToHtml(value)
        const emptyValueRegEx = /^[\r\n\s]*\<p[\s]*\>\<\/p\>[\r\n\s]*$/i

        const isEmptyValue = emptyValueRegEx.test(html)
        if (isEmptyValue) {
            if (props?.message) {
                throw new Error('A message cannot be updated as empty.')
            } else {
                throw new Error('A message is required.')
            }
        }
    }

    const handleEditorStateChange = (nextEditorState: any): void => {
        setEditorState(nextEditorState)
        form.setFieldsValue({ message: nextEditorState })
    }

    function getCurrentValues() {
        let values: any = {}

        // title
        const messageTitle = form.getFieldValue('messageTitle')
        if (!!messageTitle) {
            values.title = messageTitle
            if (deepEqual(values.title, props.message?.title)) {
                delete values.title
            }
        }

        // message
        const rawMessage = convertToRaw(editorState.getCurrentContent())
        if (!!rawMessage) {
            const html = draftToHtml(rawMessage)

            // default empty value will always contain "<p></p>↵"
            const emptyValueRegEx = /^[\r\n\s]*\<p[\s]*\>\<\/p\>[\r\n\s]*$/i

            const isEmptyValue = emptyValueRegEx.test(html)
            if (!isEmptyValue) {
                values.message = html
                if (deepEqual(values.message, props.message?.message)) {
                    delete values.message
                }
            }
        }

        // minVersionRequired?
        const minVersionRequired = form.getFieldValue('minVersionReq')
        if (useMinVersionReq) {
            values.minVersionReq = minVersionRequired
            if (deepEqual(values.minVersionReq, props.message?.min_version_req)) {
                delete values.minVersionReq
            }
        }

        // IsMaintenanceMode?
        if (!!values.title || !!values.messageTitle) {
            if (useMaintenanceMode) {
                values.maintenanceMode = useMaintenanceMode
                if (deepEqual(values.maintenanceMode, props.message?.maintenanceMode)) {
                    delete values.maintenanceMode
                }
            }
        }

        // Timeframe
        const startTime = form.getFieldValue('startTime')
        const endTime = form.getFieldValue('endTime')
        if (!!startTime) {
            values.startTime = startTime.toISOString()
            if (deepEqual(values.startTime, props.message?.start_time)) {
                delete values.startTime
            }
        }
        if (!!endTime) {
            values.endTime = endTime.toISOString()
            if (deepEqual(values.endTime, props.message?.end_time)) {
                delete values.endTime
            }
        }

        // Domain IDs
        const domainIds = form.getFieldValue('domainIds')
        if (domainRestricted) {
            values.domainIds = domainIds
            if (deepEqual(values.domainIds, props.message?.domain_ids)) {
                delete values.domainIds
            }
        }

        // Tags
        const tags = form.getFieldValue('tags')
        if (tags?.length > 0) {
            values.tags = tags
            if (deepEqual(values.tags, props.message?.tags)) {
                delete values.tags
            }
        }

        return values
    }

    async function submit(): Promise<void> {
        const mergedValues = getCurrentValues()

        if (props.message?.id) {
            const updated = await appService.updateMessage(mergedValues, props.message?.id)
            if (updated && props.onMessageUpdated) {
                props.onMessageUpdated()
            }
        } else {
            const created = await appService.createMessage(mergedValues)
            if (created && props.onMessageUpdated) {
                props.onMessageUpdated()
            }
        }
    }

    const handleSubmit = async (): Promise<any> => {
        try {
            await form.validateFields()
            await submit()
        } catch (err) {
            if (err) {
                simpleFormErrorNotification(err)
            }
        }
    }

    return (
        <Drawer
            className={classnames('platform-message-editor')}
            getContainer={appService.getAppContainer}
            title={isEditMode ? 'Edit Message' : 'New Message'}
            placement="right"
            closable={true}
            width={800}
            onClose={props.onClose}
            open={props.visible}
            onSubmit={handleSubmit}
        >
            <Form
                form={form}
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                className="hide-inline-errors"
                onFinish={handleSubmit}
                size="small"
                labelAlign="left"
                requiredMark={false}
            >
                <Well title="Message Details" mode="ghost" footerMode="hidden" padding="none">
                    <Form.Item
                        label="Title"
                        name="messageTitle"
                        initialValue={props.message?.title}
                        rules={[{ required: true, message: 'A title is required.' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Message"
                        name="message"
                        initialValue={editorState}
                        rules={[
                            {
                                required: !message,
                                validator: validateMessage,
                            },
                        ]}
                    >
                        <Editor
                            editorState={editorState}
                            toolbar={{
                                options: ['inline', 'link'],
                                inline: {
                                    inDropdown: false,
                                    options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace'],
                                },
                            }}
                            defaultEditorState={editorState}
                            toolbarClassName="pmsg-toolbar"
                            wrapperClassName="pmsg-wrapper"
                            editorClassName="pmsg-editor"
                            onEditorStateChange={handleEditorStateChange}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Start Time"
                        name="startTime"
                        initialValue={props.message ? moment(props.message?.start_time) : null}
                        rules={[{ required: true, message: 'Start Date is required.' }]}
                    >
                        <DatePicker showTime={{ format: 'HH:mm' }} format={'ll LT'} />
                    </Form.Item>

                    <Form.Item
                        label="End Time"
                        name="endTime"
                        initialValue={props.message?.end_time ? moment(props.message?.end_time) : null}
                        rules={[{ validator: validateEndDate }]}
                    >
                        <DatePicker showTime={{ format: 'HH:mm' }} format={'ll LT'} />
                    </Form.Item>

                    <Form.Item label="Restrict to Domains">
                        <Flex gap={8} align="center">
                            <Switch
                                checked={domainRestricted}
                                disabled={isEditMode}
                                onChange={(nextDomainRestricted) => {
                                    setDomainRestricted(nextDomainRestricted)
                                    if (nextDomainRestricted) {
                                        setUseMaintenanceMode(false)
                                        setUseMinVersionReq(false)
                                    }
                                }}
                            />
                            {domainRestricted && (
                                <Form.Item
                                    name="domainIds"
                                    initialValue={props.message?.domain_ids}
                                    noStyle={true}
                                >
                                    <Select mode="multiple" className="domains" style={{ flex: 1 }}>
                                        {domains?.map((domain) => (
                                            <Select.Option key={domain.id} value={domain.id}>
                                                {domain.display_name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            )}
                        </Flex>
                    </Form.Item>

                    <Form.Item label="Minimum Version Requirement">
                        <Flex gap={8} align="center">
                            <Switch
                                checked={useMinVersionReq}
                                disabled={isEditMode}
                                onChange={(nextUseMinVersionReq) => {
                                    setUseMinVersionReq(nextUseMinVersionReq)
                                    if (nextUseMinVersionReq) {
                                        setUseMaintenanceMode(false)
                                        setDomainRestricted(false)
                                    }
                                }}
                            />
                            {useMinVersionReq && (
                                <Form.Item
                                    name="minVersionReq"
                                    initialValue={minVersion}
                                    noStyle={true}
                                >
                                    <Select disabled={isEditMode} style={{ flex: 1 }}>
                                        {versions.slice().reverse().map((version) => (
                                            <Select.Option key={version} value={version}>
                                                {version}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            )}
                        </Flex>
                    </Form.Item>

                    <Form.Item label="Maintenance Mode">
                        <Switch
                            checked={useMaintenanceMode}
                            disabled={isEditMode}
                            onChange={(nextUseMaintenanceMode) => {
                                setUseMaintenanceMode(nextUseMaintenanceMode)
                                if (nextUseMaintenanceMode) {
                                    setUseMinVersionReq(false)
                                    setDomainRestricted(false)
                                }
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Tags"
                        name="tags"
                        initialValue={props.message?.tags ? props.message.tags : []}
                    >
                        <Select mode="multiple" disabled={isEditMode}>
                            <Select.Option key={TAGS.AUTOMATED} value={TAGS.AUTOMATED}>
                                {titleCase(TAGS.AUTOMATED)}
                            </Select.Option>
                            <Select.Option key={TAGS.DEMO_DOMAINS} value={TAGS.DEMO_DOMAINS}>
                                {titleCase(TAGS.DEMO_DOMAINS)}
                            </Select.Option>
                        </Select>
                    </Form.Item>
                </Well>
            </Form>
        </Drawer>
    )
}
